import styled, { css, keyframes } from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { COMPOSER_TAB_STRIP_PX } from '../ComposerRailTabs/ComposerRailTabs.styles';
import { lockedRailPassThrough } from '../NewVersionLink';
import { MatureVideoChip } from './MatureVideoChip';
import {
  ANIM,
  BRAND_DEPLOYMENT_ANIM,
  BRAND_DEPLOYMENT_GLOW,
  BRAND_DEPLOYMENT_GLOW_STOPS,
  COLORS,
  GRADIENTS,
  MOBILE,
  NAV_DESKTOP,
  REDUCED_MOTION,
  SPACE,
  STATUS_COLORS,
  STORYBOARD_CANVAS,
  STORYBOARD_COMPOSE,
  STORYBOARD_REVEAL,
} from '@/nova/theme/tokens';

/** The easy rail's Prompt section: the shared TextArea's input on its own (the section header is its label). */
export { Input as EasyPromptInput } from '@/nova/components/TextArea/TextArea.styles';

/* The storyboard composer: a toolbar (promotion, template, add/reset), the node canvas, then a footer
   summary + generate CTA, with a progress/result block beneath. Colours come only from theme tokens.

   On desktop the composer is the IMMERSIVE, Miro-style editor: the canvas fills the full viewport below the
   top bar (the layout gutters/card collapse, see MainLayout ImmersiveLayout) and the toolbar + footer FLOAT
   as pills over the board (React Flow Panels, see StoryboardCanvas). On mobile it stays a normal-flow stacked
   column (toolbar / canvas / footer / status). The `$immersive` prop drives the difference. */
export const Root = styled.div<{ $immersive?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 520px;

  /* Immersive (desktop): the board fills from content-top (its own live tucked top, published by MainLayout)
     down to the viewport floor, so height = 100dvh - content-top. The page column padding is 0 in immersive
     (MainLayout ImmersiveLayout), so the board extends UP under the transparent top bar (the header scrim fades
     it there). --content-top folds in the impersonation strip + verify-email banner. Gated on the prop (not a
     bare NAV_DESKTOP) so a standalone CT mount (no MainLayout, no --content-top) stays boxed + hermetic. */
  ${(p) =>
    p.$immersive &&
    css`
      ${NAV_DESKTOP} {
        height: calc(100dvh - var(--content-top, 24px));
        overflow: hidden;
        gap: 0;
      }
    `}
`;

/* The controls row (promotion / template / model + add/reset). On mobile it is a stacked Root child; on
   desktop it is the top row of the combined bottom floating pill (see StoryboardCanvas FooterPill). */
export const Toolbar = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
`;

/** A labelled Select flexes to share the row; drops to full width when the row wraps. */
export const ToolField = styled.div`
  flex: 1 1 180px;
  min-width: 160px;
`;

export const ToolbarButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

/* The left control panel content (immersive: a fixed floating pill filling the column where the side menu
   sits; mobile: the top of the stacked column): promotion picker, a free-text prompt that steers the LLM
   compose, and the Regenerate action. A vertical stack. */
export const LeftControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/** The art style and mascot pickers' row under the mood board: the pills at their natural width. Each hides without
 *  a choice to offer, and the row goes with them (so the column's gap never doubles). */
export const MascotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};

  &:empty {
    display: none;
  }
`;

/* The immersive bottom bar: a single compact row (template - video model - generate) that reads as a slim
   pill over the board rather than a full-width toolbar. Wraps on a narrow pill. */
export const BottomBar = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 12px;
`;

/* A select wrapper for the bottom bar: a fixed-ish basis so the two selects stay compact (they do NOT
   stretch edge-to-edge, which is what made the old bar feel oversized). */
export const BarField = styled.div`
  flex: 0 1 190px;
  min-width: 150px;
`;

/* The footer row (generate CTA + summary). On mobile it is a stacked Root child; on desktop it is the top
   content of the bottom floating pill (see StoryboardCanvas FooterPill), with the status/failure strip below. */
export const Footer = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const SummaryText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/* Sheen sweep travelling across the run progress fill (matches the onboarding deploy strip's ProgressFill,
   BrandDeploymentStrip.styles.ts, change one and check the other). */
const deployProgressSheen = keyframes`
  0% {
    transform: translateX(-100%);
  }
  60%,
  100% {
    transform: translateX(220%);
  }
`;

/* The run progress bar in the bottom bar, styled like the onboarding "Deploying" bar: a neutral track holding
   the Lumio aurora fill (purple -> pink -> cyan) with a soft white sheen sweeping across it while the run is in
   flight. The track stays neutral so it sits inside the composer's pill, the gradient + sheen carry the look. */
export const DeployProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  overflow: hidden;
`;

export const DeployProgressFill = styled.div<{ $pct: number }>`
  position: relative;
  height: 100%;
  /* Floored at 3% so the fill (and its sheen) is visibly animating from the first stage, when the job is still
     at 0 completed beats, instead of an empty track under "Preparing Scene 1". */
  width: ${(p) => Math.max(3, Math.min(100, p.$pct))}%;
  border-radius: inherit;
  overflow: hidden;
  background: ${GRADIENTS.deploymentProgress};
  transition: width ${BRAND_DEPLOYMENT_ANIM.PROGRESS_MS}ms cubic-bezier(0.22, 1, 0.36, 1);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GRADIENTS.deploymentProgressSheen};
    transform: translateX(-100%);
    animation: ${deployProgressSheen} ${BRAND_DEPLOYMENT_ANIM.PROGRESS_SHEEN_MS}ms ease-in-out infinite;
  }

  ${REDUCED_MOTION} {
    transition: none;

    &::after {
      animation: none;
    }
  }
`;

/* The run status / failure strip beneath the footer bar. It may shrink (flex-shrink:1) and scroll internally
   on desktop rather than push the pinned footer off-screen or get clipped by the column's overflow:hidden —
   the toolbar, canvas and footer stay put whatever state this is in. */
export const StatusBlock = styled.div`
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${NAV_DESKTOP} {
    min-height: 0;
    overflow-y: auto;
  }
`;

export const HintText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textFaint};
`;

export const FailText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${STATUS_COLORS.negative};
`;

/** Holds the "Resume from beat N" action under the failure notice; keeps the button at its natural
 *  width instead of stretching in the column. */
export const ResumeRow = styled.div`
  display: flex;
`;

export const EmptyText = styled(Text).attrs({ tag: 'paragraph' as const })`
  font-size: 14px;
  color: ${COLORS.textMuted};
  text-align: center;
  padding: 24px 12px;
`;

/* ── Easy mode (the default modal): setup rail + read-only beats on the left, output + Generate on the right ── */

export const EasyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
`;

/** Left cluster of the header: the title/pencil plus the Version selector, kept together and left-aligned so
 *  the version reads as governing the whole ad (the actions sit opposite via the header's space-between). */
export const EasyHeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex: 0 1 auto;
  min-width: 0;
`;

/** The title + a small pencil affordance so the owner sees the name is editable (mirrors the immersive
 *  TopBar's NameEditWrap). Shrinks/ellipsises as one unit so the pencil stays beside the name. */
export const EasyTitleWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 1 auto;
  min-width: 0;
`;

export const EasyTitleEditIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 2px;
  border: 0;
  background: transparent;
  color: ${COLORS.textFaint};
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.accent};
  }
`;

/** The video ad's name, renamed inline from the modal header. Reads as the title until hovered/focused. */
export const EasyTitleInput = styled.input`
  flex: 0 1 auto;
  min-width: 0;
  /* Size the box to the actual text width (exact in Chromium) so the hover pill + the pencil after it
     hug the title instead of trailing the input's over-wide default; the JSX size attribute is the
     cross-browser fallback. */
  field-sizing: content;
  border: 0;
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  padding: 5px 6px;
  border-radius: 8px;
  text-overflow: ellipsis;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.hoverWash};
  }
  &:focus {
    outline: none;
    background: ${COLORS.surfaceInset};
  }
  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

/** A rail coming in on a phone's tab switch: a short fade and slide from its side (what it makes from the right, the
 *  Ingredients from the left), settling to no transform at all. */
const railEnterFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(${SPACE[16]});
  }
  to {
    opacity: 1;
    transform: none;
  }
`;
const railEnterFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-${SPACE[16]});
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** The setup rail's widest, the split's left column. */
const EASY_RAIL_W = 340;
/** What the output pane takes from its column beside the rail: its hairline border and left padding (EasyOutput) and
 *  its scroll's right padding (EasyOutputScroll). */
const EASY_OUTPUT_INSET_PX = 1 + 16 + 4;
/** Room for a classic (non-overlay) scrollbar on the output's scroll, which takes width when its content scrolls. */
const EASY_OUTPUT_SCROLLBAR_PX = 16;
/** How much of the split's width the output's content does NOT get beside the rail: the rail's column, the output
 *  pane's own insets and its scrollbar. A composer whose output needs `n` px asks for tabs once the split is narrower
 *  than this + n. */
export const EASY_SPLIT_BESIDE_RAIL_PX = EASY_RAIL_W + EASY_OUTPUT_INSET_PX + EASY_OUTPUT_SCROLLBAR_PX;

/** The rail a tab hides (the other one stays mounted, only hidden), for the phone and for `$tabbed`. */
const otherRailHidden = (tab: 'ingredients' | 'output') => css`
  & > :nth-child(${tab === 'output' ? 1 : 2}) {
    display: none;
  }
`;

/** The shown rail's entrance after a switch (useRailEnter): it was display: none, so the animation starts as it
 *  appears. */
const railEntrance = (tab: 'ingredients' | 'output', from: 'left' | 'right') => css`
  & > :nth-child(${tab === 'output' ? 2 : 1}) {
    animation: ${from === 'right' ? railEnterFromRight : railEnterFromLeft} ${ANIM.COMPOSER_RAIL_ENTER_MS}ms
      ${ANIM.EASE};
  }
`;

/** Fills the sheet stage; each rail scrolls INTERNALLY so the two move independently and the sheet never
 *  gets a second outer scrollbar (mirrors EditCreativeSheet's SplitLayout). `$tabbed`: what the composer makes does
 *  not fit beside the rail (an HTML5 banner's sizes in a narrow sheet), so one rail shows at a time under the tabs, as
 *  on a phone, at any width. */
export const EasySplit = styled.div<{
  $mobileTab?: 'ingredients' | 'output';
  $railEnter?: 'left' | 'right';
  $tabbed?: boolean;
}>`
  display: grid;
  grid-template-columns: minmax(0, ${EASY_RAIL_W}px) minmax(0, 1fr);
  /* The sheet's stage (the backdrop's height, see Sheet) minus its chrome: header, body padding and the backdrop's
     gutter, ~200px with headroom. */
  height: calc(var(--sheet-stage-height, 100dvh) - 200px);
  align-items: stretch;

  /* Tabbed beside a wide sheet: one column under the strip, which takes its height out of the stage. Each rail drops
     the inset it keeps from the other (as on a phone); doubled so it outranks the rails' own rules. */
  ${(p) =>
    p.$tabbed &&
    p.$mobileTab &&
    css`
      grid-template-columns: minmax(0, 1fr);
      height: calc(var(--sheet-stage-height, 100dvh) - 200px - ${COMPOSER_TAB_STRIP_PX}px);

      && > * {
        padding-right: 0;
        padding-left: 0;
        border-left: none;
      }

      ${otherRailHidden(p.$mobileTab)}
    `}

  ${MOBILE} {
    grid-template-columns: 1fr;
    height: auto;

    /* A phone shows one rail at a time (ComposerRailTabs). */
    ${(p) => (p.$mobileTab ? otherRailHidden(p.$mobileTab) : null)}
  }

  /* After a switch the shown rail enters from its side, wherever the tabs show. Never on the sheet's open. */
  ${(p) =>
    p.$mobileTab &&
    p.$railEnter &&
    css`
      ${p.$tabbed
        ? railEntrance(p.$mobileTab, p.$railEnter)
        : css`
            ${MOBILE} {
              ${railEntrance(p.$mobileTab, p.$railEnter)}
            }
          `}

      ${REDUCED_MOTION} {
        & > :nth-child(${p.$mobileTab === 'output' ? 2 : 1}) {
          animation: none;
        }
      }
    `}
`;

export const EasyRail = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding-right: 16px;

  ${MOBILE} {
    padding-right: 0;
  }
`;

/** The scrolling middle of the left rail: the setup as the Art Style page's ingredient sections, one open at a time
 *  (each draws its own divider, so the stack has no gap; like the image banner editor's rail). */
export const EasyRailScroll = styled.div<{ $locked?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Reserve the scrollbar's room up front: opening a long section showed a scrollbar that narrowed the column and
     shifted every row sideways. */
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  /* Room for the open section's background, which reaches past the column on both sides. */
  padding: 0 ${SPACE[12]};

  /* On a locked (rendered) version every field is disabled: clicks fall through them to this container, whose
     onClick shakes the "New version from this" link (the shared locked-rail behaviour). */
  ${(p) => p.$locked && lockedRailPassThrough}
`;

/** A short reason pinned above the left rail's foot while the storyboard button waits (the promotions' text in the
 *  ad's language is being prepared, or could not be, with Try again). */
export const EasyRailNote = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${SPACE[8]} ${SPACE[12]} 0;
`;

export const EasyRailNoteText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/** Pinned bottom of the left rail: the Generate/Regenerate storyboard button. Same top border + padding as
 *  the right foot, and both rails are equal height, so the two footer rows line up exactly. */
export const EasyRailFoot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding-top: 14px;
  margin-top: 2px;
  border-top: 1px solid ${COLORS.hairline};
`;

export const EasyOutput = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding-left: 16px;
  border-left: 1px solid ${COLORS.hairline};
  /* A size container so the footer controls stack by the PANE width (not the viewport): the modal narrows with
   * the window, so a viewport media query would miss a cramped-but-not-mobile pane. See EasyOutputFoot. */
  container-type: inline-size;

  ${MOBILE} {
    padding-left: 0;
    border-left: none;
  }
`;

/** The scrolling middle of the right rail: the output player + run status. */
export const EasyOutputScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
  /* overflow-y: auto clips horizontally too, and the beat timeline dots sit flush left with a ~13px pulsing glow
     while generating. This padding gives the glow room inside the clip; the matching negative margin keeps the
     content exactly where it was (on desktop it borrows the pane's own 16px left padding). */
  padding-left: 14px;
  margin-left: -14px;
`;

// Below this pane width the locked foot's facts and action stack, as do the left rail's foot controls.
const EASY_FOOT_STACK_W = 500;

// The width a footer dropdown (video model, aspect ratio) asks for before it would rather wrap than shrink.
const EASY_FOOT_SELECT_BASIS_W = 160;

/* One-shot entrance for the output foot: the row fades in (so its hairline border comes with it) while the
   three controls gently rise into place, staggered, so Generate settles last. Fires on mount, which is exactly
   when the storyboard has finished composing + revealing (the foot is not rendered before then). */
const footFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const footRise = keyframes`
  from {
    transform: translateY(10px) scale(0.99);
  }
  to {
    transform: translateY(0) scale(1);
  }
`;

/** Pinned bottom of the right rail: video model, aspect ratio and Generate in one row while Generate's label
 *  (nowrap, with its cost) fits beside them, vertically aligned with the left rail's foot. Generate never shrinks
 *  below its label: when the row cannot hold it, it wraps to its own full-width line under the dropdowns, and a
 *  narrower pane stacks the dropdowns too. The break follows the button's real width (its label, cost and
 *  language), not a fixed pane width. */
export const EasyOutputFoot = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  /* The dropdowns share what is left of the row; Generate is the third child. */
  & > :nth-child(1),
  & > :nth-child(2) {
    flex: 1 1 ${EASY_FOOT_SELECT_BASIS_W}px;
    min-width: 0;
  }
  & > :nth-child(3) {
    flex: 1 0 auto;
    width: auto;
  }
  padding-top: 14px;
  margin-top: 2px;
  border-top: 1px solid ${COLORS.hairline};
  animation: ${footFadeIn} ${STORYBOARD_COMPOSE.FOOT_ENTER_MS}ms ${ANIM.EASE} both;

  /* Each control rises independently, staggered, for an organic settle (Generate is the third child). */
  & > * {
    animation: ${footRise} ${STORYBOARD_COMPOSE.FOOT_ENTER_MS}ms ${ANIM.EASE} both;
  }
  & > *:nth-child(2) {
    animation-delay: ${STORYBOARD_COMPOSE.FOOT_ENTER_STAGGER_MS}ms;
  }
  & > *:nth-child(3) {
    animation-delay: ${STORYBOARD_COMPOSE.FOOT_ENTER_STAGGER_MS * 2}ms;
  }

  ${REDUCED_MOTION} {
    animation: none;
    & > * {
      animation: none;
    }
  }
`;

/** The mature-style chip spans the whole foot row (model, aspect and Generate). */
export const EasyFootChip = styled(MatureVideoChip)`
  flex-basis: 100%;
`;

/** Foot of a rendered (locked) version: the model + aspect are fixed for this cut, so they read as static facts
 *  on the left and the only action is branching a new version on the right. Same top border/padding as the
 *  editable EasyOutputFoot so switching between a draft and a rendered version doesn't shift the row. */
export const EasyLockedFoot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 14px;
  margin-top: 2px;
  border-top: 1px solid ${COLORS.hairline};

  @container (max-width: ${EASY_FOOT_STACK_W}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

/** The read-only model · aspect facts on the left of a locked foot. */
export const EasyFootFacts = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
`;

export const EasyFootFact = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textMuted};
  white-space: nowrap;
`;

export const EasyFootFactDot = styled.span`
  color: ${COLORS.textFaint};
`;

/** While a video run is in flight the right-pane footer swaps to a progress bar + stage + Stop (mirroring the
 *  advanced bottom bar), replacing the model/aspect/Generate row. Same border/padding so the swap is seamless. */
export const EasyRunFoot = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 14px;
  margin-top: 2px;
  border-top: 1px solid ${COLORS.hairline};
`;

export const EasyRunFootRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

/** The finished ad, contained (letterboxed for whichever aspect) on a neutral plate. */
export const EasyVideo = styled.video<{ $ready: boolean }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  object-fit: contain;
  /* Fades in once the first frame has decoded, instead of popping out of an empty box. */
  opacity: ${(p) => (p.$ready ? 1 : 0)};
  transition: opacity ${ANIM.VIDEO_READY_FADE_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* Version picker above the finished-ad player (easy mode), shown only when the ad has more than one render. Left
   aligned and width-capped so the dropdown stays compact over the player. */
export const EasyVersionRow = styled.div`
  align-self: flex-start;
  width: 100%;
  max-width: 300px;
`;

/* The output's stage: the finished ad in flow, the run preview laid over it while the ad reveals (VideoReveal). */
export const EasyOutputStage = styled.div`
  position: relative;
  width: 100%;
`;

const previewDissolve = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

export const EasyOutputEmpty = styled.div<{ $over?: boolean; $dissolving?: boolean }>`
  position: relative;
  /* Own stacking context, so the hint's z-index (which lifts it over the dot field) stays local to this box and
     can't paint over the footer's upward-opening model / aspect menus. */
  isolation: isolate;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px dashed ${COLORS.hairline};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;

  /* Over the finished ad while it reveals: laid on top of it (same box), then dissolving just behind the sweep. */
  ${({ $over }) =>
    $over &&
    css`
      position: absolute;
      inset: 0;
      pointer-events: none;
    `}
  ${({ $dissolving }) =>
    $dissolving &&
    css`
      animation: ${previewDissolve} ${STORYBOARD_REVEAL.AFTERGLOW_FADE_MS}ms ${ANIM.EASE}
        ${STORYBOARD_REVEAL.AFTERGLOW_DELAY_MS}ms both;

      ${REDUCED_MOTION} {
        animation: none;
        opacity: 0;
      }
    `}
`;

export const EasyOutputHint = styled(Text).attrs({ tag: 'span' as const })`
  position: relative;
  z-index: 1;
  font-size: 13px;
  /* Bright: it sits over the dot field and, during a run, the frame strip (the faint grey read poorly there). */
  color: ${COLORS.textBright};
  /* Protect the copy against the atlas dot-field behind it (light, semi-opaque dots over the dark inset bed).
     A single blurred halo spreads too thin to cover them, so we stack several layers in the panel's own inset
     colour: repeated small radii build near-full opacity right around each glyph (a real knockout, not a faint
     wash) and the wider radii feather it out. Same colour as the background, so it reads as a bigger clean bed,
     not a hard outline. */
  text-shadow:
    0 0 3px ${COLORS.surfaceInset},
    0 0 3px ${COLORS.surfaceInset},
    0 0 6px ${COLORS.surfaceInset},
    0 0 6px ${COLORS.surfaceInset},
    0 0 10px ${COLORS.surfaceInset},
    0 0 14px ${COLORS.surfaceInset},
    0 0 18px ${COLORS.surfaceInset};
`;

/* Guided empty state for the right rail when there is no storyboard yet (and none is composing): ONE
   centered block that walks the owner through the flow, replacing the idle finished-ad placeholder + the
   empty beats list so the pane reads as a single clear "here's what to do next" instead of two blank boxes. */
export const EasyGuide = styled.div`
  flex: 1 1 auto;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;
  padding: 32px 20px;
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px dashed ${COLORS.hairline};
`;

export const EasyGuideIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

export const EasyGuideTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const EasyGuideText = styled(Text).attrs({ tag: 'span' as const })`
  max-width: 340px;
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** The three numbered steps, mirroring the actual flow (set up, generate storyboard, generate video). */
export const EasyGuideSteps = styled.ol`
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
`;

export const EasyGuideStep = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const EasyGuideStepNum = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: ${COLORS.textMuted};
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

export const EasyGuideStepText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/* Beats timeline: dot markers connected by a vertical line, each with the role + a one-liner of its prompt. */
export const EasyBeats = styled.div<{ $stale?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 4px 0 2px;
  transition:
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    filter ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  /* Stale: the setup changed after these beats were composed, so they no longer match. Dimmed + desaturated
     so it reads as "out of date, regenerate" without hiding what is there. */
  ${(p) =>
    p.$stale &&
    css`
      opacity: 0.4;
      filter: grayscale(0.6);
    `}
`;

/** A caution line above the timeline when the storyboard is stale, in the same amber the advanced canvas uses
 *  for a beat queued to be re-run. */
export const EasyStaleNote = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  margin: -4px 0 12px;
  font-size: 12px;
  color: ${STORYBOARD_CANVAS.staleBorder};
`;

/** Wraps the storyboard timeline where it sits in the right pane, under the video: a hairline divides it
 *  from the player + status above. */
export const EasyBeatsSection = styled.div`
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid ${COLORS.hairline};
`;

export const EasyBeatsHead = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  margin-bottom: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
`;

/* --- The easy-mode compose state, in the studio's own "being built for you right now" language ----------
   The whole studio expresses generation ONE way: a soft masked-conic Lumio border travelling around the
   thing being built (BounceCard / GeneratingArticle / the canvas node cards, all keyed off
   BRAND_DEPLOYMENT_GLOW(_STOPS)) and a slow breath that travels the chain (STORYBOARD_COMPOSE.SEED_*). The
   easy timeline echoes that, rather than a generic shimmer:
     - while composing, each skeleton beat softly breathes in sequence (a wave down the timeline), its dot a
       diffuse Lumio orb, its lines a quiet monochrome placeholder (no rainbow, no hard sweep);
     - when the beats land, each row focuses in (blur + rise) and its dot blooms once, soft, not a ring. */

/* One row's slow breath while the model writes; staggered per beat so a soft wave travels down the chain. */
const seedBreath = keyframes`
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 1;
  }
`;

/* The skeleton dot's diffuse Lumio orb, breathing in step with its row. A blurred glow (blur radius), never
   a hard expanding ring. */
const seedOrb = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 ${STORYBOARD_CANVAS.lumioEdgeGlowCyan};
    opacity: 0.5;
  }
  50% {
    box-shadow: 0 0 12px 1px ${STORYBOARD_CANVAS.lumioEdgeGlowCyan};
    opacity: 1;
  }
`;

/* Each real beat row focuses in on mount (when composing ends and the timeline swaps in): a soft blur + rise,
   not a slide. */
const beatFocusIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(9px);
    filter: blur(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

/* A single soft bloom around a dot as its beat lands (blur radius, fading to nothing) then settles to rest. */
const dotBloom = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${STORYBOARD_CANVAS.lumioEdgeGlowCyan};
  }
  35% {
    box-shadow: 0 0 14px 2px ${STORYBOARD_CANVAS.lumioEdgeGlowPink};
  }
  100% {
    box-shadow: 0 0 0 0 ${STORYBOARD_CANVAS.lumioEdgeGlowPink};
  }
`;

/* The connector INTO the beat currently rendering softly breathes in Lumio, echoing the canvas's active edge. */
const edgeBreath = keyframes`
  0%,
  100% {
    box-shadow: 0 0 3px 0 ${STORYBOARD_CANVAS.lumioEdgeGlowCyan};
    opacity: 0.75;
  }
  50% {
    box-shadow: 0 0 9px 1px ${STORYBOARD_CANVAS.lumioEdgeGlowCyan};
    opacity: 1;
  }
`;

/** One beat row in the timeline. During a video run `$run` mirrors the canvas node status: the connectors
 *  fill in as a progress trail, the beat being rendered breathes in Lumio, and beats still ahead recede. */
export const EasyBeatRow = styled.div<{ $reveal?: number; $run?: 'idle' | 'active' | 'done' | 'stale' }>`
  position: relative;
  display: flex;
  gap: 10px;
  padding-bottom: 14px;
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  ${(p) =>
    p.$reveal != null &&
    css`
      animation: ${beatFocusIn} ${STORYBOARD_COMPOSE.EASY_REVEAL_MS}ms ${ANIM.EASE}
        ${p.$reveal * STORYBOARD_COMPOSE.EASY_REVEAL_STAGGER_MS}ms both;
    `}
  /* Beats the run has not reached yet recede so the eye stays on the one rendering. */
  ${(p) =>
    p.$run === 'idle' &&
    css`
      opacity: 0.5;
    `}

  &:last-child {
    padding-bottom: 0;
  }
  /* Connector line: from this dot down to the next; hidden on the last row. */
  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 15px;
    bottom: 0;
    width: 2px;
    background: ${COLORS.hairline};
    transform: translateX(-50%);
    transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
  ${(p) =>
    p.$run === 'done' &&
    css`
      &::before {
        background: ${COLORS.accent};
      }
    `}
  ${(p) =>
    p.$run === 'active' &&
    css`
      &::before {
        background: ${COLORS.accent};
        animation: ${edgeBreath} ${STORYBOARD_COMPOSE.EDGE_BLOOM_MS}ms ${ANIM.EASE} infinite;
      }
    `}
  &:last-child::before {
    display: none;
  }
  ${REDUCED_MOTION} {
    animation: none;
    &::before {
      animation: none;
    }
  }
`;

/** The timeline marker for one beat. On reveal it blooms once; while its beat renders it is a breathing Lumio
 *  orb (the same active-node signal the canvas uses). */
export const EasyBeatDot = styled.span<{ $reveal?: number; $run?: 'idle' | 'active' | 'done' | 'stale' }>`
  flex: 0 0 auto;
  width: 11px;
  height: 11px;
  margin-top: 4px;
  border-radius: 999px;
  /* Gray until the beat's video is rendered: a done/active beat lights up in the accent, everything else
     (a previewed-but-not-generated storyboard) reads as a quiet, inactive dot. */
  background: ${(p) => (p.$run === 'done' || p.$run === 'active' ? COLORS.accent : COLORS.textMuted)};
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  z-index: 1;
  ${(p) =>
    p.$run === 'active'
      ? css`
          animation: ${seedOrb} ${STORYBOARD_COMPOSE.SEED_PULSE_MS}ms ${ANIM.EASE} infinite;
        `
      : p.$reveal != null &&
        css`
          animation: ${dotBloom} ${STORYBOARD_COMPOSE.EASY_DOT_BLOOM_MS}ms ${ANIM.EASE}
            ${p.$reveal * STORYBOARD_COMPOSE.EASY_REVEAL_STAGGER_MS + STORYBOARD_COMPOSE.EASY_DOT_BLOOM_DELAY_MS}ms both;
        `}
  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** One skeleton timeline row shown while Lumio composes. The row breathes in sequence (a wave down the
 *  chain); the whole thing is quiet and monochrome apart from the dot's soft Lumio orb. */
export const EasyBeatSkelRow = styled.div<{ $index: number }>`
  position: relative;
  display: flex;
  gap: 10px;
  padding-bottom: 14px;
  animation: ${seedBreath} ${STORYBOARD_COMPOSE.SEED_PULSE_MS}ms ${ANIM.EASE}
    ${(p) => p.$index * STORYBOARD_COMPOSE.SEED_STAGGER_MS}ms infinite;

  &:last-child {
    padding-bottom: 0;
  }
  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 15px;
    bottom: 0;
    width: 2px;
    background: ${COLORS.hairline};
    transform: translateX(-50%);
  }
  &:last-child::before {
    display: none;
  }
  ${REDUCED_MOTION} {
    animation: none;
    opacity: 0.7;
  }
`;

export const EasyBeatSkelDot = styled.span<{ $index: number }>`
  flex: 0 0 auto;
  width: 11px;
  height: 11px;
  margin-top: 4px;
  border-radius: 999px;
  background: ${COLORS.accent};
  z-index: 1;
  animation: ${seedOrb} ${STORYBOARD_COMPOSE.SEED_PULSE_MS}ms ${ANIM.EASE}
    ${(p) => p.$index * STORYBOARD_COMPOSE.SEED_STAGGER_MS}ms infinite;
  ${REDUCED_MOTION} {
    animation: none;
    opacity: 0.6;
  }
`;

export const EasyBeatSkelBars = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 2px;
`;

/* Quiet monochrome placeholder lines (the row itself supplies the breath); the two widths stand in for the
   role + the motion one-liner. */
const skelBar = css`
  height: 9px;
  border-radius: 6px;
  background: ${COLORS.surfaceInset};
`;

export const EasyBeatSkelRole = styled.span`
  ${skelBar}
  width: 38%;
`;

export const EasyBeatSkelText = styled.span`
  ${skelBar}
  width: 78%;
  opacity: 0.6;
`;

export const EasyBeatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
`;

export const EasyBeatRole = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${COLORS.text};
`;

/** A one-liner of the beat's prompt (its motion), truncated to a single line. */
export const EasyBeatMotion = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** A run failure shown INLINE under the beat that failed: full, wrapping text in the danger colour (no cap, no
 *  ellipsis, no inner scroll, unlike the immersive StatusBlock). */
export const EasyBeatError = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${STATUS_COLORS.negative};
`;

/* A direct "Retry" text button under the inline failure copy (easy mode). Accent-coloured so it reads as the
   recovery action against the danger-red error above it. */
export const EasyRetryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${COLORS.accent};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/** The top notice for a run that stopped or failed BEFORE a beat (cancel / assembly): a plain block that wraps
 *  fully within the pane's own scroll, never its own scroller. */
export const EasyNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
`;

/* The studio's one "being built for you right now" signal: a soft Lumio arc travelling around the edge of the
   thing being built. Replicated (not shared) from BounceCard / GeneratingArticle / the canvas node cards, all
   keyed off BRAND_DEPLOYMENT_GLOW(_STOPS) so it reads identically everywhere. The `from <angle>` is an ANIMATED
   custom property (registered by CanvasGlowAngleProperty, rendered in the easy Sheet), so it cannot be a static
   GRADIENTS token. */
const btnGlowSpin = keyframes`
  to {
    --sb-glow-angle: 360deg;
  }
`;
const BTN_GLOW_RING = `conic-gradient(from var(--sb-glow-angle), transparent 0 ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[0]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.62}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[1]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.36}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[2]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.09}%, transparent 100%)`; // design-allow
const BTN_RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)'; // design-allow

/** The left-rail action button. A local pill (matching the shared Button) so the compose action can lead with
 *  the LUMIO glyph, like Generate Video, instead of a generic Icon (the shared Button only takes an IconName).
 *  While `$working` a soft Lumio arc travels around its edge (the studio's shared generation signal) and it
 *  stays fully opaque despite being disabled, so the compose reads as active work. */
export const EasyStoryboardBtn = styled.button<{ $primary: boolean; $working?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  box-sizing: border-box;
  height: 48px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 18px;
  border-radius: 99px;
  font-size: 16px;
  cursor: pointer;
  ${(p) =>
    p.$primary
      ? css`
          background: ${COLORS.ctaSolid};
          border: 1px solid ${COLORS.ctaSolid};
          color: ${COLORS.ctaSolidText};
          font-weight: 600;
        `
      : css`
          background: ${COLORS.ctaOutlineBg};
          border: 1px solid ${COLORS.ctaBorder};
          color: ${COLORS.ctaText};
        `}

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  /* Travelling Lumio arc hugging the pill edge: always present but transparent, so it FADES in when composing
     starts rather than popping. A soft GLOW (blur), not a border. */
  &::after {
    content: '';
    position: absolute;
    inset: -1px;
    z-index: 0;
    border-radius: inherit;
    padding: 1.5px;
    background: ${BTN_GLOW_RING};
    -webkit-mask: ${BTN_RING_MASK};
    mask: ${BTN_RING_MASK};
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    filter: blur(2.5px);
    opacity: 0;
    transition: opacity ${BRAND_DEPLOYMENT_GLOW.FADE_MS}ms ease;
    pointer-events: none;
  }

  ${(p) =>
    p.$working &&
    css`
      cursor: progress;
      /* Settle to a neutral card surface while working (like the canvas node cards), so the Lumio arc reads on
         a dark pill instead of muddying the solid CTA, and stay fully opaque despite being disabled. */
      background: ${COLORS.surfaceCard};
      border-color: ${COLORS.panelBorder};
      color: ${COLORS.text};
      &:disabled {
        opacity: 1;
      }
      &::after {
        opacity: ${BRAND_DEPLOYMENT_GLOW.ALPHA};
        animation: ${btnGlowSpin} ${BRAND_DEPLOYMENT_GLOW.SPIN_MS}ms linear infinite;
      }
      ${REDUCED_MOTION} {
        &::after {
          animation: none;
        }
      }
    `}
`;

/* Wraps the leading glyph so it sits above the travelling arc. */
export const EasyBtnGlyph = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
`;

export const EasyStoryboardBtnText = styled(Text).attrs({ tag: 'span' as const })`
  position: relative;
  z-index: 1;
  font-weight: inherit;
`;
